import React, { useCallback } from 'react';
import { createUseStyles, useTheme } from 'react-jss';

import { Theme } from '@app/theme';
import * as Yup from 'yup';

import { CenteredModal, Input, SubmitButton, useToast } from 'components';
import { Form, Formik, FormikHelpers } from 'formik';
import { useInviteMemberToOrganization } from 'queries';
import { ApiValidationFailedException } from 'store/modules/exceptions';
import { useTranslation } from 'react-i18next';

const useStyles = createUseStyles(() => ({
  button: {
    textAlign: 'right',
  },
}));

const initialValues = {
  email: '',
};

const validationSchema = Yup.object().shape({
  email: Yup.string().email().required(),
});

interface Props {
  organizationId?: string;
  onRequestClose: () => void;
  isOpen: boolean;
}

export const AddMemberModal = ({ isOpen, onRequestClose }: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const toast = useToast();
  const { t } = useTranslation();

  const { mutateAsync: inviteToOrganization } = useInviteMemberToOrganization();

  const handleSubmit = useCallback(
    async (values: { email: string }, { setStatus }: FormikHelpers<any>) => {
      try {
        await inviteToOrganization(values);
        onRequestClose();
        toast.success(t('teamInformations.invited'));
      } catch (e) {
        toast.exception(e);
        if (e instanceof ApiValidationFailedException) {
          setStatus(e.getFormikErrors());
        }
      }
    },

    [inviteToOrganization, onRequestClose, t, toast],
  );

  return (
    <CenteredModal
      onRequestClose={onRequestClose}
      isOpen={isOpen}
      title={t('teamInformations.inviteModal.title')}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        <Form>
          <div>
            <Input
              name="email"
              label={t('teamInformations.inviteModal.emailLabel')}
              type="email"
            />
          </div>
          <div className={classes.button}>
            <SubmitButton
              text={t('teamInformations.inviteModal.submitButton')}
            />
          </div>
        </Form>
      </Formik>
    </CenteredModal>
  );
};
